import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Grid } from "@mui/material";
import { toast } from "react-toastify";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { DateObject } from "react-multi-date-picker";
import { LocaleContext } from "../../../../routing/LangRouter.js";

//import utils
import { fixNumbers, navigateToLoginPage } from "../../../../utils/utility.js";

//import constants
import BASE_URL from "../../../../constants/baseUrls.js";

//import general components
import { BaseButton } from "../../../../components/common/base-button/base-button";
import BaseTextField from "../../../common/base-text-field/base-text-field";
import AutoCompleteComponent from "../../../common/base-select/autocomplete-component.js";
import SingleDatePicker from "../../../common/single-date-picker/single-date-picker.jsx";

//import styles
import "./personal-information.scss";

function PersonalInformation({ userInformation }) {
    //general variables
    const navigate = useNavigate();
    const location = useLocation();
    const { locale } = useContext(LocaleContext);
    const { t } = useTranslation();
    const accessToken = localStorage.getItem("accessToken");
    const [loading, setLoading] = useState(false);
    const [isFocused, setIsFocused] = useState({});

    //country list request
    const [countrySearchValue, setCountrySearchValue] = useState("");
    const [countryList, setCountryList] = useState([]);
    const getCountryList = async () => {
        const response = await axios.get(`${BASE_URL}/api/v2/basic/countries/`);
        setCountryList(response.data.data.list);
    };
    useEffect(() => {
        getCountryList();
    }, []);

    //options gender
    const [genderSearchValue, setGenderSearchValue] = useState("");
    const GENDER_OPTION = [
        { id: 1, name: t("male") },
        { id: 2, name: t("female") },
    ];

    const [educationSearchValue, setEducationSearchValue] = useState("");
    const EDUCATION_List = [
        { id: 1, name: t("Education Levels.Below High School") },
        { id: 2, name: t("Education Levels.High School Diploma") },
        { id: 3, name: t("Education Levels.Associate Degree") },
        { id: 4, name: t("Education Levels.Bachelor's Degree") },
        { id: 5, name: t("Education Levels.Master's Degree") },
        { id: 6, name: t("Education Levels.Doctorate") },
        { id: 7, name: t("Education Levels.Postdoctoral") },
    ];

    const formList = [
        { id: 1, label: t("firstName"), name: "first_name", fieldType: "text", type: "input", dir: "rtl" },
        { id: 2, label: t("lastName"), name: "last_name", fieldType: "text", type: "input", dir: "rtl" },
        { id: 3, label: t("firstNameEn"), name: "english_first_name", fieldType: "text", type: "input", dir: "ltr" },
        { id: 4, label: t("lastNameEn"), name: "english_last_name", fieldType: "text", type: "input", dir: "ltr" },
        { id: 5, label: t("fatherName"), name: "father_name", fieldType: "text", type: "input", dir: "rtl" },
        { id: 6, label: t("email"), name: "email", fieldType: "text", type: "input", dir: "ltr" },
        // { id: 7, label: t("phoneNumber"), name: "phone_number", fieldType:""  ,type: "input", dir: "ltr" },
        { id: 8, label: t("nationalCode"), name: "national_code", fieldType: "number", type: "input", dir: "ltr" },
        { id: 9, label: t("gender"), name: "gender", type: "select", options: GENDER_OPTION, setInputValue: setGenderSearchValue },
        { id: 10, label: t("nationality"), name: "country", type: "select", options: countryList, setInputValue: setCountrySearchValue },
        { id: 11, label: t("education"), name: "education", type: "select", options: EDUCATION_List, setInputValue: setEducationSearchValue },
        { id: 12, label: t("birthday"), name: "birthdate", type: "date", maxDate: new Date(), isPersianCalendar: true },
    ];

    //handle form submit
    const {
        register,
        reset,
        handleSubmit,
        getValues,
        control,
        formState: { errors, dirtyFields },
    } = useForm({
        defaultValues: userInformation,
        mode: "onChange",
        delayError: 500,
    });

    useEffect(() => {
        reset(userInformation);
    }, [reset, userInformation]);

    const handleRegistration = async formValues => {
        const newData = Object.keys(dirtyFields).reduce(
            (acc, dirtyField) => {
                if (dirtyField === "country" || dirtyField === "gender" || dirtyField === "education") {
                    return acc;
                }
                return {
                    ...acc,
                    [dirtyField]: fixNumbers(formValues?.[dirtyField]) || null,
                };
            },
            {
                nationality: formValues?.country?.id,
                education: formValues?.education?.id,
                gender: formValues?.gender?.id,
                passport_issue_country: formValues?.passport_issue_country?.id,
            },
        );
        try {
            setLoading(true);
            const response = await axios.patch(`${BASE_URL}/api/v2/account/user/info/`, newData, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": "application/json",
                },
            });

            reset(response.data.data);
            toast.success(t("requestSubmittedSuccessfully"), {
                toastId: "custom-id-yes",
            });
        } catch (error) {
            setLoading(false);
            if (error.response.status === 401) {
                localStorage.clear();
                navigateToLoginPage(navigate, locale, location);
            } else {
                toast.error(t("Toast Messages.Error Occurred"), { toastId: "custom-id-yes" });
            }
        } finally {
            setLoading(false);
        }
    };
    const handleError = error => console.log(error);

    //form validations
    const registerOptions = {
        first_name: {
            minLength: { value: 2, message: `${t("firstNameValidation1")}` },
            pattern: { value: /^[\u0600-\u06FF\s]+$/, message: `${t("firstNameValidation2")}` },
        },
        last_name: {
            minLength: { value: 2, message: `${t("firstNameValidation1")}` },
            pattern: { value: /^[\u0600-\u06FF\s]+$/, message: `${t("firstNameValidation2")}` },
        },
        english_first_name: {
            minLength: { value: 2, message: `${t("firstNameEnValidation1")}` },
            pattern: { value: /^[A-Za-z][A-Za-z0-9]*$/, message: `${t("firstNameENValidation2")}` },
        },
        english_last_name: {
            minLength: { value: 2, message: `${t("lastNameEnValidation1")}` },
            pattern: { value: /^[A-Za-z][A-Za-z0-9]*$/, message: `${t("lastNameEnValidation1")}` },
        },
        phone_number: {
            maxLength: { value: 11, message: `${t("phoneNumberValidation1")}` },
            minLength: { value: 11, message: `${t("phoneNumberValidation2")}` },
            pattern: { value: /^(09|\u06F0\u06F9)[0-9\u06F0-\u06F9]{9}$/, message: `${t("phoneNumberValidation3")}` },
        },
        email: {
            pattern: {
                value: /^[a-zA-Z0-9_\u06F0-\u06F9.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z]{2,}$/,
                message: `${t("email")} ${t("isWrong")}`,
            },
        },
        gender: {},
        country: {},
        education: {},
        national_code: {
            // required: `${t("nationalCode")} ${t("isRequired")}`,
            validate: {
                validateNumber: nationalId => {
                    nationalId = fixNumbers(nationalId);
                    if (!nationalId) {
                        return true;
                    }

                    // Check length is 10
                    if (nationalId.length < 8 || 10 < nationalId.length) {
                        return `${t("nationalCode")} ${t("isWrong")}`;
                    }

                    //Check if all of the numbers are the same
                    if (
                        nationalId === "0000000000" ||
                        nationalId === "1111111111" ||
                        nationalId === "2222222222" ||
                        nationalId === "3333333333" ||
                        nationalId === "4444444444" ||
                        nationalId === "5555555555" ||
                        nationalId === "6666666666" ||
                        nationalId === "7777777777" ||
                        nationalId === "8888888888" ||
                        nationalId === "9999999999"
                    ) {
                        return `${t("nationalCode")} ${t("isWrong")}`;
                    }

                    // STEP 00 : if nationalId.length==8 add two zero on the left
                    if (nationalId.length < 10) {
                        let zeroNeeded = 10 - nationalId.length;
                        let zeroString = "0".repeat(zeroNeeded);
                        nationalId = zeroString + nationalId;
                    }

                    // STEP 1: Sum all numbers
                    let sum = 0;
                    for (let i = 0; i < 9; i++) {
                        sum += parseInt(nationalId.charAt(i)) * (10 - i);
                    }

                    // STEP 2: MOD ON 11
                    let mod = sum % 11;

                    // STEP 3: Check with 2
                    let finalValue = mod >= 2 ? 11 - mod : mod;

                    // STEP 4: Final Step check with control value
                    if (finalValue !== parseInt(nationalId.charAt(9))) {
                        return `${t("nationalCode")} ${t("isWrong")}`;
                    }

                    const foundNationalCode = getValues("national_code") === nationalId;
                    if (foundNationalCode && foundNationalCode.length > 1) {
                        return `${t("nationalCodeValidation")}`;
                    }

                    return true;
                },
            },
        },
    };

    return (
        <Grid className="personal-information" container item xs={12}>
            <Grid className="personal-information__title-box" container item xs={12} display={{ xs: "none", lg: "flex" }}>
                <span className="personal-information__title">{t("persoanlDetails")}</span>
            </Grid>
            <Grid
                className="personal-information__form"
                component="form"
                container
                item
                xs={12}
                rowGap={{ xs: 3, md: 4 }}
                justifyContent="space-between"
                onSubmit={handleSubmit(handleRegistration, handleError)}
            >
                {formList.map(item => {
                    return (
                        <Grid key={item?.id} container item md={3.8} alignItems="flex-end">
                            {item.type === "input" && (
                                <BaseTextField
                                    dir={item.dir}
                                    label={item.label}
                                    name={item.name}
                                    type={item.fieldType}
                                    register={{ ...register(item.name, registerOptions?.[item?.name]) }}
                                    error={errors?.[item?.name]?.message}
                                    radius="10px"
                                    focused={!!getValues(`${item.name}`) || isFocused[item.name]}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, [item.name]: true }))}
                                />
                            )}

                            {item.type === "select" && (
                                <AutoCompleteComponent
                                    label={item.label}
                                    options={item.options}
                                    name={item.name}
                                    defaultValue={getValues(`${item.name}`)}
                                    setInputValue={item.setInputValue}
                                    control={control}
                                    register={registerOptions?.[item?.name]}
                                    errors={errors?.[item?.name]?.message}
                                    width="auto"
                                />
                            )}

                            {item.type === "date" && (
                                <SingleDatePicker
                                    label={item.label}
                                    name={`${item.name}`}
                                    size="medium"
                                    isPersianCalendar={item?.isPersianCalendar && locale === "fa"}
                                    defaultValue={new DateObject(new Date(getValues(`${item.name}`) || ""))}
                                    minDate={item?.minDate}
                                    maxDate={item?.maxDate}
                                    format="YYYY/MM/DD"
                                    control={control}
                                    register={registerOptions[item.name]}
                                    errors={errors?.[item.name]?.message || errors?.[item.name]?.message}
                                />
                            )}
                        </Grid>
                    );
                })}

                <Grid container item md={3.8}>
                    <BaseButton text={t("submitInformation")} color="primary" size="medium" radius="radius-10" loading={loading} />
                </Grid>
            </Grid>
        </Grid>
    );
}

export default PersonalInformation;
